// DevHub UI kit — list state that survives leaving the page.
//
// Every browse page (docs, catalog, APIs, MCP marketplace, landscape…) held its search box and
// filters in plain `useState`. FlatRoutes unmounts the route element on navigation, so opening a
// result and pressing Back dropped the query and every filter — you started over each time. Putting
// the state in the URL fixes that for free: Back restores the previous entry's params, a reload keeps
// them, and a filtered view becomes a link you can share.
//
// Drop-in for `useState<string>`:
//
//   const [search, setSearch] = useQueryParamState('q');
//   const [kind, setKind] = useQueryParamState('kind');
//
// Follows `useAdminTab`'s convention (plugins/admin AdminTabs.tsx): writes REPLACE the history entry,
// so typing in a filter doesn't bury the page you came from under dozens of back-steps.
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Read/write one query parameter as component state.
 *
 * @param param - the query-string key (keep it short: `q`, `kind`, `owner`)
 * @param fallback - the value when the param is absent
 */
export function useQueryParamState(
  param: string,
  fallback = '',
): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(param) ?? fallback;

  const setValue = useCallback(
    (next: string) => {
      // The FUNCTIONAL updater matters: a "clear all" sets several params in one tick, and a version
      // that closed over `searchParams` would compute each write from the same stale snapshot and
      // drop its siblings. This always merges into the latest params.
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          // An empty value means "no filter", so drop the key rather than leaving `?q=` behind — an
          // untouched page keeps a clean URL.
          if (next === '' || next === fallback) params.delete(param);
          else params.set(param, next);
          return params;
        },
        { replace: true },
      );
    },
    [param, fallback, setSearchParams],
  );

  return [value, setValue];
}
